import "./App.css";
import { useEffect, useMemo, useState } from "react";
import useReveal from "./hooks/useReveal";

export default function App() {
  useReveal();

  const [billing, setBilling] = useState("yearly"); // "monthly" | "yearly"
  const rotatingTexts = [
  "Chatbot Assistant",
  "SEO Booster",
  "Product Description AI",
  "AI Content Writer",
  "Workflow Automation",
];

const [textIndex, setTextIndex] = useState(0);
const [textAnim, setTextAnim] = useState(true);

useEffect(() => {
  const interval = setInterval(() => {
    setTextAnim(false);
    setTimeout(() => {
      setTextIndex((prev) => (prev + 1) % rotatingTexts.length);
      setTextAnim(true);
    }, 120);
  }, 1700);

  return () => clearInterval(interval);
}, []);

  const pricing = useMemo(() => {
    const isYearly = billing === "yearly";
    return {
      starter: isYearly ? 190 : 29,
      pro: isYearly ? 490 : 49,
      enterprise: "Custom",
      suffix: isYearly ? "/ Year" : "/ Month",
    };
  }, [billing]);

  return (
    <div className="app">
      {/* Background */}
      <div className="bg">
        <div className="blob blob-purple" />
        <div className="blob blob-blue" />
        <div className="blob blob-gold" />
        <div className="gridLines" />
        <div className="noise" />
      </div>

      {/* NAVBAR */}
      <header className="nav">
        <div className="brand">
          <div className="brandIcon">
            <span />
            <span />
            <span />
          </div>
          <div className="brandText">TarzonAI</div>
        </div>

        <nav className="navLinks">
          <a href="#how">How it Works</a>
          <a href="#tools">AI Tools</a>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">Help</a>
          <a href="#About">About</a>
        </nav>

        <div className="navActions">
          <button className="btn btnGhost">Log in</button>
          <button className="btn btnPrimary">Get Started</button>
        </div>
      </header>

      {/* MAIN */}
      <main className="container">
        {/* HERO */}
        <section className="hero">
          <div className="heroLeft" data-reveal>
            <div className="pill">
              <span className="dot" /> AI SaaS • Business Ready • Ultra Fast
            </div>

   <h1 className="heroTitle">
  Start your{" "}
  <span className={`midBlink ${textAnim ? "show" : ""}`}>
    {rotatingTexts[textIndex]}
  </span>
  <br />
  in Minutes!
</h1>

            <p className="heroDesc">
              TarzonAI lets you launch AI-driven tools for content generation, smart automation,
              analytics and modern workflows — with a premium UI.
            </p>

            <div className="heroButtons">
              <button className="btn btnPrimary big">Get Started</button>
              <button className="btn btnGhost big">View Demo</button>
            </div>

            {/* Mini KPI */}
            <div className="kpis" data-reveal>
              <div className="kpi">
                <div className="kpiNum">10x</div>
                <div className="kpiText">Faster Launch</div>
              </div>
              <div className="kpi">
                <div className="kpiNum">99.9%</div>
                <div className="kpiText">Uptime</div>
              </div>
              <div className="kpi">
                <div className="kpiNum">24/7</div>
                <div className="kpiText">Support</div>
              </div>
            </div>

            {/* Orbit Card */}
            <div className="orbitCard" data-reveal>
              <div className="orbitHead">
                <h3>I Need AI Engineers</h3>
                <p>
                  Build your AI product with premium UI, lead capture, pricing, and deployment
                  ready in one setup.
                </p>
              </div>

              <div className="orbitArea">
                <div className="orbitRing r1" />
                <div className="orbitRing r2" />
                <div className="orbitRing r3" />

                <div className="orb orb1" />
                <div className="orb orb2" />
                <div className="orb orb3" />
                <div className="orb orb4" />
              </div>
            </div>
          </div>

          {/* AI Center */}
          <div className="heroCenter" data-reveal>
            <div className="aiCard">
              <div className="aiTop">
                <div className="chip">TarzonAI Core</div>
                <div className="chip live">LIVE</div>
              </div>

              <div className="aiObject">
                <div className="coreGlow" />
                <div className="coreBox">
                  <div className="coreGrid">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <span key={i} />
                    ))}
                  </div>
                </div>

                <div className="energy e1" />
                <div className="energy e2" />
                <div className="energy e3" />
              </div>

              <div className="aiBottom">
                <h4>Smart Capabilities</h4>
                <div className="capList">
                  <div className="cap">⚡ AI Content Generation</div>
                  <div className="cap">🔐 Secure Deployments</div>
                  <div className="cap">📈 Analytics & Insights</div>
                  <div className="cap">🤖 Workflow Automation</div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <aside className="heroRight" id="pricing" data-reveal>
            <div className="pricingCard">
              <div className="pricingTop">
                <div className="tag">Starter Plan</div>

                {/* Billing Toggle */}
                <div className="toggleWrap">
                  <button
                    className={`toggleBtn ${billing === "monthly" ? "active" : ""}`}
                    onClick={() => setBilling("monthly")}
                  >
                    Monthly
                  </button>
                  <button
                    className={`toggleBtn ${billing === "yearly" ? "active" : ""}`}
                    onClick={() => setBilling("yearly")}
                  >
                    Yearly
                  </button>
                </div>

                <div className="price">
                  <span className="money">
                    {typeof pricing.starter === "number" ? `$${pricing.starter}` : pricing.starter}
                  </span>
                  <span className="year">{pricing.suffix}</span>
                </div>

                <p className="muted">
                  {billing === "yearly" ? "Best value yearly pack 🔥" : "Flexible monthly plan ✅"}
                </p>
              </div>

              <div className="pricingList">
                <label className="tick">
                  <input type="checkbox" defaultChecked />
                  <span>Custom domain support</span>
                </label>
                <label className="tick">
                  <input type="checkbox" defaultChecked />
                  <span>Modern AI landing theme</span>
                </label>
                <label className="tick">
                  <input type="checkbox" defaultChecked />
                  <span>Priority email support</span>
                </label>
                <label className="tick">
                  <input type="checkbox" defaultChecked />
                  <span>SEO + Speed optimized</span>
                </label>
                <label className="tick">
                  <input type="checkbox" defaultChecked />
                  <span>AI chatbot (coming soon)</span>
                </label>
              </div>

              <button className="btn btnPrimary full">Start Now</button>
              <p className="tiny">No credit card required</p>
            </div>
          </aside>
        </section>

        {/* AI Tools Marquee */}
        <section className="section" id="tools" data-reveal>
          <div className="sectionHead">
            <h2>AI Tools Included</h2>
            <p>Auto-scrolling tools row — premium SaaS style.</p>
          </div>

          <div className="marquee">
            <div className="marqueeTrack">
              {[
                "AI Content Writer",
                "Image Generator",
                "Chatbot Assistant",
                "Resume Builder",
                "Product Description AI",
                "Voice Script AI",
                "SEO Booster",
                "AI Analytics",
                "Workflow Automation",
              ].map((t, i) => (
                <div key={i} className="toolPill">
                  ✨ {t}
                </div>
              ))}
              {/* duplicate for smooth infinite */}
              {[
                "AI Content Writer",
                "Image Generator",
                "Chatbot Assistant",
                "Resume Builder",
                "Product Description AI",
                "Voice Script AI",
                "SEO Booster",
                "AI Analytics",
                "Workflow Automation",
              ].map((t, i) => (
                <div key={`d-${i}`} className="toolPill">
                  ✨ {t}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW */}
        <section className="section" id="how" data-reveal>
          <div className="sectionHead">
            <h2>How TarzonAI Empowers Your Business?</h2>
            <p>Premium design + conversion sections + deploy ready system.</p>
          </div>

          <div className="cards3">
            <div className="card">
              <div className="iconBox">🧠</div>
              <h3>AI Modules</h3>
              <p>Build content tools, chatbots, automation and analytics.</p>
            </div>

            <div className="card">
              <div className="iconBox">⚡</div>
              <h3>Fast UI</h3>
              <p>Glass UI, neon gradients, animations, and modern layout.</p>
            </div>

            <div className="card">
              <div className="iconBox">🚀</div>
              <h3>Deploy in Minutes</h3>
              <p>Build → Hosting deploy → Domain connected. Done.</p>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="section" id="features" data-reveal>
          <div className="sectionHead">
            <h2>Advanced Features</h2>
            <p>Built for high conversion + premium AI branding.</p>
          </div>

          <div className="grid4">
            <div className="fCard">
              <h4>✨ Glassmorphism UI</h4>
              <p>Blur cards, depth shadows and premium gradients.</p>
            </div>
            <div className="fCard">
              <h4>🌈 Neon Gradient System</h4>
              <p>Modern background blending like top AI websites.</p>
            </div>
            <div className="fCard">
              <h4>🧩 Ready Sections</h4>
              <p>Hero, tools, features, pricing, FAQ, footer.</p>
            </div>
            <div className="fCard">
              <h4>📱 Full Responsive</h4>
              <p>Mobile + tablet + desktop perfect fit.</p>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="section" id="testimonials" data-reveal>
          <div className="sectionHead">
            <h2>What People Say</h2>
            <p>Trust-building section for more conversions </p>
          </div>

          <div className="testGrid">
            <div className="tCard">
              <div className="stars">★★★★★</div>
              <p>
                “TarzonAI landing looks like a premium SaaS. Deploy was super smooth on Firebase.”
              </p>
              <div className="tUser">
                <div className="avatar">R</div>
                <div>
                  <div className="name">Rohan</div>
                  <div className="role">Founder</div>
                </div>
              </div>
            </div>

            <div className="tCard">
              <div className="stars">★★★★★</div>
              <p>
                “The UI is ultra clean, and pricing section increases conversions instantly.”
              </p>
              <div className="tUser">
                <div className="avatar">AI</div>
                <div>
                  <div className="name">Team Client</div>
                  <div className="role">Startup</div>
                </div>
              </div>
            </div>

            <div className="tCard">
              <div className="stars">★★★★★</div>
              <p>
                “Best glassmorphism + neon gradients. Perfect for AI business.”
              </p>
              <div className="tUser">
                <div className="avatar">TA</div>
                <div>
                  <div className="name">TarzonAI</div>
                  <div className="role">Product</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section" id="faq" data-reveal>
          <div className="sectionHead">
            <h2>Help Center</h2>
            <p>Quick answers for launch.</p>
          </div>

          <div className="faq">
            <details className="faqItem">
              <summary>Website update ka easiest method?</summary>
              <p>
                <b>npm run build</b> then <b>firebase deploy</b>
              </p>
            </details>

            <details className="faqItem">
              <summary>tarzonai.com Is coming soon....</summary>
              <p>
               
              </p>
            </details>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="footer" data-reveal>
          <div className="footerInner">
            <div className="footerBrand">
              <div className="brandText">TarzonAI</div>
              <p>Premium AI landing UI • Built with React + Firebase.</p>
            </div>

            <div className="footerLinks">
              <a href="#how">How it Works</a>
              <a href="#tools">AI Tools</a>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">Help</a>
            </div>
          </div>

          <div className="copyright">© 2026 TarzonAI. All rights reserved.</div>
        </footer>
      </main>
    </div>
  );
}